import {
  GUARDIANSHIP_FIELD_LIMITS,
  VA_FIELD_LIMITS,
  PROBATE_FIELD_LIMITS,
  TRUSTEE_FIELD_LIMITS,
} from "../../../globals/utils/bondFieldLimits";
import logger from "../../../globals/utils/logger";
import { parseCurrencyToNumber, safeNumber } from "./formHelpers";
import {
  appendAttorneyRequiredFields,
  appendHighBondAuthField,
  ATTORNEY_REQUIRED_FIELDS,
  REQUESTOR_REQUIRED_FIELDS,
} from "./bondFormValidationHelpers";
import {
  buildFirstLastSupportName,
  buildPrincipalNameSaveName,
  buildRequiredFieldsPerStepFactory,
} from "./bondFormKindBase";

export const BOND_FORM_STEPS = [
  "Basic Information",
  "General Information",
  "Principal Information",
  "Attorney Information",
  "Obligee Information",
  "Requestor Information",
  "Document Upload",
  "Billing Information",
];

export const ROLE_HOME_MAP = {
  super_user: "/admin_home",
  internal_user: "/internal_user_home",
  law_firm_user: "/law_firm_user_home",
  law_firm_admin: "/law_firm_admin_home",
  carrier_team_member: "/carrier_team_member_dashboard",
  "professional guardian": "/professional_guardian_home",
  pg_contact: "/pg_contact_home",
};

export const GUARDIANSHIP_STEP_LIMITS_MAP = {
  0: GUARDIANSHIP_FIELD_LIMITS.basicInformation,
  1: GUARDIANSHIP_FIELD_LIMITS.generalInformation,
  2: GUARDIANSHIP_FIELD_LIMITS.principalInformation,
  5: GUARDIANSHIP_FIELD_LIMITS.requestorInformation,
  7: GUARDIANSHIP_FIELD_LIMITS.billingInformation,
};

export const VA_STEP_LIMITS_MAP = {
  0: VA_FIELD_LIMITS.basicInformation,
  1: VA_FIELD_LIMITS.generalInformation,
  2: VA_FIELD_LIMITS.principalInformation,
  5: VA_FIELD_LIMITS.requestorInformation,
  7: VA_FIELD_LIMITS.billingInformation,
};

export const PROBATE_STEP_LIMITS_MAP = {
  0: PROBATE_FIELD_LIMITS.basicInformation,
  1: PROBATE_FIELD_LIMITS.generalInformation,
  2: PROBATE_FIELD_LIMITS.principalInformation,
  5: PROBATE_FIELD_LIMITS.requestorInformation,
  7: PROBATE_FIELD_LIMITS.billingInformation,
};

export const TRUSTEE_STEP_LIMITS_MAP = {
  0: TRUSTEE_FIELD_LIMITS.basicInformation,
  1: TRUSTEE_FIELD_LIMITS.generalInformation,
  2: TRUSTEE_FIELD_LIMITS.principalInformation,
  5: TRUSTEE_FIELD_LIMITS.requestorInformation,
  7: TRUSTEE_FIELD_LIMITS.billingInformation,
};

/**
 * @typedef {Object} BondFormDuplicateCheckConfig
 * @property {boolean} enabled
 * @property {string} endpoint
 * @property {string} nestedKey
 * @property {string} principalsKey
 */

/**
 * @typedef {Object} BondFormValidationConfig
 * @property {number} principalStep
 * @property {string} [principalCountField]
 * @property {string[]} [principalFieldNames]
 * @property {"standard" | "guardianship"} [principalFieldMode]
 * @property {boolean} [requirePrincipalEmailOnEsign]
 * @property {"dash" | "separators"} [splitEmploymentOccupation]
 * @property {string} [occupationKey]
 */

/**
 * @typedef {Object} BondFormRequiredFieldsContext
 * @property {number} step
 * @property {object} data
 * @property {object} state
 * @property {boolean} isGuestRequest
 */

/**
 * @typedef {Object} BondFormKindConfig
 * @property {string} payloadKind
 * @property {string} guestPathPrefix
 * @property {string} bondTypeLabel
 * @property {string} failedBondType
 * @property {string} cacheRoutePrefix
 * @property {string} updateRoutePrefix
 * @property {string} createEndpoint
 * @property {BondFormDuplicateCheckConfig} duplicateCheck
 * @property {Record<number, object>} stepLimitsMap
 * @property {object} basicInfoDefaults
 * @property {(globalState: object) => string} getSaveName
 * @property {(globalState: object) => string} getSupportName
 * @property {string} untitledSaveName
 * @property {BondFormValidationConfig} validation
 * @property {(ctx: BondFormRequiredFieldsContext) => string[]} getRequiredFieldsPerStep
 * @property {(globalState: object) => unknown[]} getPremiumRecalcInputs
 */

/** @type {Record<string, BondFormKindConfig>} */
export const BOND_FORM_KIND_CONFIG = {
  guardianship: {
    payloadKind: "guardianship",
    guestPathPrefix: "/vermost-guardianship-bond-request-guest",
    bondTypeLabel: "Guardianship",
    failedBondType: "Guardianship",
    cacheRoutePrefix: "/vermost-guardianship-bond-request-cache/",
    updateRoutePrefix: "/vermost-guardianship-bond-update/",
    createEndpoint: "/create_guardianship_bond/",
    duplicateCheck: {
      enabled: true,
      endpoint: "/check_duplicate_guardianship_bond/",
      nestedKey: "guardianshipbond",
      principalsKey: "guardians",
    },
    stepLimitsMap: GUARDIANSHIP_STEP_LIMITS_MAP,
    basicInfoDefaults: {},
    untitledSaveName: "Untitled Guardianship Bond",
    getSaveName: buildPrincipalNameSaveName,
    getSupportName: buildFirstLastSupportName,
    validation: {
      principalStep: 2,
      principalFieldMode: "guardianship",
      occupationKey: "guardian_occupation",
      splitEmploymentOccupation: "separators",
    },
    getRequiredFieldsPerStep: buildRequiredFieldsPerStepFactory({
      baseFieldsByStep: {
        0: [
          "bondSubCategory",
          "county",
          "firstName",
          "lastName",
          "dateOfDeath",
          "bondAmount",
          "GuardianNumber",
        ],
        1: [],
        2: [],
        3: [],
        4: ["obligeeId"],
        5: [...REQUESTOR_REQUIRED_FIELDS],
        6: [],
        7: ["billing_entity"],
      },
    }),
    getPremiumRecalcInputs: (globalState) => [
      globalState.basicInformation?.dateOfDeath,
      globalState.basicInformation?.bondSubCategory,
    ],
  },
  va: {
    payloadKind: "va",
    guestPathPrefix: "/vermost-va-bond-request-guest",
    bondTypeLabel: "VA",
    failedBondType: "VA",
    cacheRoutePrefix: "/vermost-va-bond-request-cache/",
    updateRoutePrefix: "/vermost-va-bond-update/",
    createEndpoint: "/create_va_bond/",
    duplicateCheck: {
      enabled: true,
      endpoint: "/check_duplicate_va_bond/",
      nestedKey: "vabond",
      principalsKey: "principals",
    },
    stepLimitsMap: VA_STEP_LIMITS_MAP,
    basicInfoDefaults: { is_esignatures: "No" },
    untitledSaveName: "Untitled VA Bond",
    getSaveName: buildPrincipalNameSaveName,
    getSupportName: buildFirstLastSupportName,
    validation: {
      principalStep: 2,
      principalCountField: "PrincipalNumber",
      requirePrincipalEmailOnEsign: true,
      splitEmploymentOccupation: "dash",
    },
    getRequiredFieldsPerStep: ({ step, data, state, isGuestRequest }) => {
      if (step === 3 && isGuestRequest) return [];
      const baseFields = {
        0: [
          "firstName",
          "lastName",
          "dateOfBirth",
          "bondAmount",
          "PrincipalNumber",
          ...(data?.hasCourtCaseNumber === "Yes" ? ["courtCaseNumber"] : []),
          "is_esignatures",
        ],
        1: [],
        2: [],
        3: [],
        4: ["obligeeId"],
        5: [...REQUESTOR_REQUIRED_FIELDS],
        6: ["courtOrderLink"],
        7: ["billing_entity"],
      };
      const fields = [...(baseFields[step] || [])];
      if (step === 3) appendAttorneyRequiredFields(fields, data);
      if (step === 1) appendHighBondAuthField(fields, step, state);
      return fields;
    },
    getPremiumRecalcInputs: (globalState) => [
      globalState.basicInformation?.dateOfBirth,
      globalState.basicInformation?.bondSubCategory,
      globalState.basicInformation?.bondTerm,
    ],
  },
  probate: {
    payloadKind: "probate",
    guestPathPrefix: "/vermost-probate-bond-request-guest",
    bondTypeLabel: "Probate",
    failedBondType: "Probate",
    cacheRoutePrefix: "/vermost-probate-bond-request-cache/",
    updateRoutePrefix: "/vermost-probate-bond-update/",
    createEndpoint: "/create_probate_bond/",
    duplicateCheck: {
      enabled: true,
      endpoint: "/check_duplicate_probate_bond/",
      nestedKey: "probatebond",
      principalsKey: "personal_rep",
    },
    stepLimitsMap: PROBATE_STEP_LIMITS_MAP,
    basicInfoDefaults: { is_esignatures: "No" },
    untitledSaveName: "Untitled Probate Bond",
    getSaveName: buildPrincipalNameSaveName,
    getSupportName: buildFirstLastSupportName,
    validation: {
      principalStep: 2,
      principalCountField: "PrincipalNumber",
      requirePrincipalEmailOnEsign: true,
      splitEmploymentOccupation: "separators",
    },
    getRequiredFieldsPerStep: ({ step, data, state, isGuestRequest }) => {
      if (step === 3 && isGuestRequest) return [];
      const baseFields = {
        0: [
          "bondSubCategory",
          "county",
          "firstName",
          "lastName",
          "dateOfDeath",
          "bondAmount",
          "PrincipalNumber",
        ],
        1: ["didLastWillAndTestament"],
        2: [],
        3: [...ATTORNEY_REQUIRED_FIELDS],
        4: ["obligeeId"],
        5: [...REQUESTOR_REQUIRED_FIELDS],
        6: [],
        7: ["billing_entity"],
      };
      const fields = [...(baseFields[step] || [])];
      if (step === 0 && data?.bondAmount) {
        const bondAmount = safeNumber(parseCurrencyToNumber(data.bondAmount));
        if (bondAmount >= 50000) fields.push("is_esignatures");
      }
      if (step === 1) appendHighBondAuthField(fields, step, state);
      return fields;
    },
    getPremiumRecalcInputs: (globalState) => [
      globalState.basicInformation?.dateOfBirth,
      globalState.basicInformation?.bondSubCategory,
      globalState.basicInformation?.bondTerm,
    ],
  },
  trustee: {
    payloadKind: "trustee",
    guestPathPrefix: "/vermost-trustee-bond-request-guest",
    bondTypeLabel: "Trustee",
    failedBondType: "Trustee",
    cacheRoutePrefix: "/vermost-trustee-bond-request-cache/",
    updateRoutePrefix: "/vermost-trustee-bond-update/",
    createEndpoint: "/create_trustee_bond/",
    duplicateCheck: {
      enabled: true,
      endpoint: "/check_duplicate_trustee_bond/",
      nestedKey: "trustee_bond",
      principalsKey: "trustees",
    },
    stepLimitsMap: TRUSTEE_STEP_LIMITS_MAP,
    basicInfoDefaults: { is_esignatures: "No" },
    untitledSaveName: "Untitled Trustee Bond",
    getSaveName: (globalState) => globalState?.basicInformation?.legalName || "",
    getSupportName: (globalState) =>
      globalState?.basicInformation?.legalName || "Unknown",
    validation: {
      principalStep: 2,
      principalCountField: "PrincipalNumber",
      requirePrincipalEmailOnEsign: true,
      splitEmploymentOccupation: "separators",
    },
    getRequiredFieldsPerStep: buildRequiredFieldsPerStepFactory({
      baseFieldsByStep: {
        0: [
          "county",
          "legalName",
          "trustName",
          "dateOfBirth",
          "bondAmount",
          "PrincipalNumber",
          "is_esignatures",
        ],
        1: [],
        2: [],
        3: [],
        4: ["obligeeId"],
        5: [...REQUESTOR_REQUIRED_FIELDS],
        6: [],
        7: ["billing_entity"],
      },
      appendHighBondOnStep: 1,
    }),
    getPremiumRecalcInputs: (globalState) => [
      globalState.basicInformation?.dateOfBirth,
      globalState.basicInformation?.bondSubCategory,
      globalState.basicInformation?.bondTerm,
    ],
  },
};

/** @param {keyof typeof BOND_FORM_KIND_CONFIG} kind */
export function getBondFormKindConfig(kind) {
  const config = BOND_FORM_KIND_CONFIG[kind];
  if (!config) {
    throw new Error(`Unknown bond form kind: ${kind}`);
  }
  return config;
}

function readLocalStorageLabel(storageKey, logContext) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return "";
    return JSON.parse(raw).label || "";
  } catch (err) {
    logger.debug(logContext, `${storageKey} JSON parse failed`, { err });
    return "";
  }
}

function parseResumePayload(logContext) {
  const savedResume = localStorage.getItem("resumedBondPayload");
  if (!savedResume) return null;
  try {
    return JSON.parse(savedResume);
  } catch (err) {
    logger.debug(logContext, "resume payload parse failed", { err });
    return null;
  }
}

export function buildBondFormInitialState(basicInfoDefaults = {}, logContext = "buildBondFormInitialState") {
  const parsed = parseResumePayload(logContext);
  if (parsed) {
    let body = parsed.request_body;
    if (typeof body === "string") {
      try {
        body = JSON.parse(body);
      } catch (err) {
        logger.debug(logContext, "resume payload nested body parse failed", { err });
      }
    }
    if (body?.state) return body.state;
    if (!parsed.request_body) return parsed;
  }

  const stateLabel = readLocalStorageLabel("selectedState", logContext);
  const typeLabel = readLocalStorageLabel("selectedBondType", logContext);

  return {
    basicInformation: {
      state: stateLabel,
      bondType: typeLabel,
      ...basicInfoDefaults,
    },
    generalInformation: {},
    guardianDetails: { guardians: [] },
    attorneyInformation: {},
    obligeeInformation: {},
    requestorInformation: {},
    documentUpload: {},
    billingInformation: {},
  };
}

export function buildBondFormInitialStep(logContext = "buildBondFormInitialStep") {
  const parsed = parseResumePayload(logContext);
  if (!parsed) return 0;
  let body = parsed.request_body;
  if (typeof body === "string") {
    try {
      body = JSON.parse(body);
    } catch (err) {
      logger.debug(logContext, "resume payload nested body parse failed", { err });
      return 0;
    }
  }
  if (typeof body?.activeStep === "number") return body.activeStep;
  return 0;
}
